// Profit & Loss — admin-only (PNL_FEATURE_EMAILS). Spec:
// docs/superpowers/specs/2026-11-03-profit-and-loss-design.md
//
// Everything here reads the worker's cached tables through /admin/pnl/*; the
// page never triggers a Xero call. URL shape:
//   /admin/profit-loss/:tab?g=month&a=2026-10-01
// tab ∈ statement|daily|clients|orders|targets|setup, g ∈ day|month|quarter|year,
// a = first day of the anchor month.
import { useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { ChevronLeft, ChevronRight, RefreshCw } from 'lucide-react'
import { ToastContainer, useToast } from '../../components/Toast'
import { businessToday, monthStart, monthEnd, monthLabel, periodRange, shiftPeriod } from '../../lib/dates'
import {
  PNL_KEY, usePnlAccess, usePnlStatus, usePnlStatement, usePnlDaily, usePnlClients,
  usePnlSettings, usePnlAccounts,
} from '../../components/pnl/queries'
import { Seg, MissingInputs, Loading, ErrorBox } from '../../components/pnl/ui'
import KpiTiles from '../../components/pnl/KpiTiles'
import StatusChips from '../../components/pnl/StatusChips'
import StatementTab from '../../components/pnl/StatementTab'
import DailyTab from '../../components/pnl/DailyTab'
import ClientsTab from '../../components/pnl/ClientsTab'
import OrdersTab from '../../components/pnl/OrdersTab'
import TargetsTab from '../../components/pnl/TargetsTab'
import SetupTab from '../../components/pnl/SetupTab'

const TABS = [
  ['statement', 'Statement'],
  ['daily', 'Daily'],
  ['clients', 'Clients'],
  ['orders', 'Orders'],
  ['targets', 'Targets'],
  ['setup', 'Setup'],
]
const GRANS = [['day', 'Day'], ['month', 'Month'], ['quarter', 'Quarter'], ['year', 'Year']]
const minIso = (a, b) => (a < b ? a : b)

export default function ProfitLoss() {
  const { tab: tabParam } = useParams()
  const tab = TABS.some(([k]) => k === tabParam) ? tabParam : 'statement'
  const navigate = useNavigate()
  const [search, setSearch] = useSearchParams()
  const qc = useQueryClient()
  const { toasts, addToast, removeToast } = useToast()

  const today = businessToday()
  const gParam = search.get('g')
  const gran = GRANS.some(([k]) => k === gParam) ? gParam : 'month'
  const aParam = search.get('a')
  const anchorMonth = /^\d{4}-\d{2}-\d{2}$/.test(aParam || '') ? monthStart(aParam) : monthStart(today)

  const period = periodRange(gran, anchorMonth)
  const statementGran = gran === 'day' ? 'month' : gran
  // The API anchor: today when the period is still open, else its last day.
  const statementPeriod = periodRange(statementGran, anchorMonth)
  const apiAnchor = minIso(statementPeriod.to, today)
  const rangeTo = minIso(period.to, today)
  // Step to the START of the next period (a mid-period anchor would otherwise
  // overshoot and keep a period that has already started out of reach).
  const nextAnchor = periodRange(gran, shiftPeriod(gran, anchorMonth, 1)).from
  const canGoNext = nextAnchor <= today
  const monthAnchor = minIso(monthEnd(anchorMonth), today)

  const accessQ = usePnlAccess()
  const allowed = !!accessQ.data?.allowed

  const statusQ = usePnlStatus(allowed)
  const statementQ = usePnlStatement(statementGran, apiAnchor, allowed)
  const monthStatementQ = usePnlStatement('month', monthAnchor, allowed && tab === 'targets')
  const dailyQ = usePnlDaily(period.from, rangeTo, allowed && ['daily', 'targets', 'orders'].includes(tab))
  const clientsQ = usePnlClients(period.from, rangeTo, allowed && tab === 'clients')
  const settingsQ = usePnlSettings(allowed && ['targets', 'daily'].includes(tab))
  const accountsQ = usePnlAccounts(allowed && tab === 'setup')

  // Orders tab day: the user's pick, else the latest day in the period that shipped something.
  const [ordersDate, setOrdersDate] = useState(null)
  useEffect(() => { setOrdersDate(null) }, [period.from, period.to])
  const defaultOrdersDate = useMemo(() => {
    const days = dailyQ.data?.days || []
    const shipped = days.filter((d) => Number(d.orders) > 0 && d.date <= today)
    return shipped.length ? shipped[shipped.length - 1].date : rangeTo
  }, [dailyQ.data, today, rangeTo])
  // Wait for the daily read before defaulting, so we don't fetch today's orders and then jump.
  const effectiveOrdersDate = ordersDate || (dailyQ.data ? defaultOrdersDate : dailyQ.error ? rangeTo : null)

  // Back from the Xero callback: ?xero=connected or ?xero_error=<message>
  // (backend/routers/auth.py). Toast once, then strip the params.
  useEffect(() => {
    const err = search.get('xero_error') || search.get('error')
    const ok = search.get('xero') === 'connected'
    if (!err && !ok) return
    if (err) addToast({ type: 'error', message: `Xero reconnect didn't finish: ${err}`, duration: 8000 })
    else {
      addToast({ type: 'success', message: 'Xero reconnected. P&L data arrives with the next hourly refresh.' })
      qc.invalidateQueries({ queryKey: [...PNL_KEY, 'status'] })
    }
    const next = new URLSearchParams(search)
    for (const k of ['xero', 'xero_error', 'error', 'error_description']) next.delete(k)
    setSearch(next, { replace: true })
  }, [search, setSearch, addToast, qc])

  function setParams(g, a) {
    const next = new URLSearchParams(search)
    next.set('g', g)
    next.set('a', a)
    setSearch(next, { replace: false })
  }
  function goTab(t, g = gran, a = anchorMonth) {
    const next = new URLSearchParams(search)
    next.set('g', g)
    next.set('a', a)
    navigate(`/admin/profit-loss/${t}?${next.toString()}`)
  }
  function pickGran(g) {
    if (g === gran) return
    if (g === 'day') goTab('daily', g)
    else if (gran === 'day' && tab === 'daily') goTab('statement', g)
    else setParams(g, anchorMonth)
  }

  if (accessQ.isLoading) return <Loading label="Checking access…" />
  // A failed access check is an error, not "not allowed" — say so and offer a retry.
  if (accessQ.isError && !accessQ.data) {
    return (
      <div className="card p-6 text-sm">
        <ErrorBox error={accessQ.error} what="access to the Profit & Loss page" />
        <button type="button" className="btn-secondary !py-1.5" onClick={() => accessQ.refetch()} disabled={accessQ.isFetching}>
          <RefreshCw size={14} className={accessQ.isFetching ? 'animate-spin' : ''} /> Try again
        </button>
      </div>
    )
  }
  if (!allowed) {
    return (
      <div className="card p-6 text-sm text-slate-600">
        The Profit &amp; Loss page isn&apos;t available for your login.
      </div>
    )
  }

  const tabQuery = { daily: dailyQ, clients: clientsQ, targets: monthStatementQ, setup: accountsQ }[tab]
  const missing = [
    ...(statementQ.data?.missing_inputs || []),
    ...(tabQuery?.data?.missing_inputs || []),
  ]
  const currentCol = (statementQ.data?.columns || []).find((c) => c.is_current)

  return (
    <div className="max-w-[1400px] mx-auto">
      <ToastContainer toasts={toasts} onRemove={removeToast} />

      <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
        <h2 className="text-xl font-semibold text-slate-800">Profit &amp; Loss</h2>
        <div className="flex flex-wrap items-center gap-2">
          <Seg options={GRANS} value={gran} onChange={pickGran} />
          <div className="inline-flex items-center border border-slate-300 rounded-lg bg-white text-[13px] text-slate-700">
            <button
              type="button"
              className="px-2 py-1.5 text-slate-400 hover:text-slate-700"
              onClick={() => setParams(gran, shiftPeriod(gran, anchorMonth, -1))}
              aria-label="Previous period"
            >
              <ChevronLeft size={16} />
            </button>
            <span className="px-1 whitespace-nowrap">{period.label}</span>
            <button
              type="button"
              className="px-2 py-1.5 text-slate-400 hover:text-slate-700 disabled:opacity-30 disabled:hover:text-slate-400"
              onClick={() => setParams(gran, nextAnchor)}
              disabled={!canGoNext}
              aria-label="Next period"
            >
              <ChevronRight size={16} />
            </button>
          </div>
          <button
            type="button"
            className="btn-secondary !py-1.5 !px-3"
            onClick={() => qc.invalidateQueries({ queryKey: PNL_KEY })}
          >
            <RefreshCw size={14} className={statementQ.isFetching ? 'animate-spin' : ''} /> Refresh
          </button>
        </div>
      </div>

      <StatusChips status={statusQ.data} statusError={statusQ.error} columns={statementQ.data?.columns} />
      <MissingInputs items={missing} />

      {statementQ.error
        ? null /* the Statement tab shows the error; tiles would only repeat it */
        : <KpiTiles kpis={statementQ.data?.kpis} periodLabel={currentCol?.label} loading={statementQ.isLoading} />}

      <div className="flex gap-0.5 border-b border-slate-300 mb-3.5 overflow-x-auto" role="tablist">
        {TABS.map(([k, label]) => (
          <button
            key={k}
            type="button"
            role="tab"
            aria-selected={tab === k}
            onClick={() => goTab(k)}
            className={[
              'px-3.5 py-2 -mb-px border-b-2 whitespace-nowrap text-[13.5px]',
              tab === k ? 'text-blue-600 border-blue-600 font-semibold' : 'text-slate-500 border-transparent hover:text-slate-700',
            ].join(' ')}
          >
            {label}
          </button>
        ))}
      </div>

      {tab === 'statement' && <StatementTab query={statementQ} />}
      {tab === 'daily' && (
        <DailyTab
          query={dailyQ}
          periodLabel={periodRange(statementGran, anchorMonth).label}
          isMonth={statementGran === 'month'}
          targetPbitMonth={settingsQ.data?.targets?.pbit_month}
        />
      )}
      {tab === 'clients' && <ClientsTab query={clientsQ} periodLabel={periodRange(statementGran, anchorMonth).label} />}
      {tab === 'orders' && (
        <OrdersTab date={effectiveOrdersDate} setDate={setOrdersDate} maxDate={today} />
      )}
      {tab === 'targets' && (
        <TargetsTab
          settingsQ={settingsQ}
          monthQ={monthStatementQ}
          dailyQ={dailyQ}
          monthLabel={monthLabel(anchorMonth)}
          addToast={addToast}
        />
      )}
      {tab === 'setup' && <SetupTab statusQ={statusQ} accountsQ={accountsQ} addToast={addToast} />}
    </div>
  )
}
